import {Grid} from "@material-ui/core";
import FormHelperText from "@material-ui/core/FormHelperText";
import * as PropTypes from "prop-types";
import React, {useEffect, useState} from "react";
import {BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBD} from "../../../consts/funktionaleRollen";
import {logger} from "../../../services/LogService";
import {getAsEinheitEndpoint, getAsParameterEndpoint} from "../../../services/WertelistenService";
import {createCalculatedColumn, createColumn, createComponentColumn, createNumberColumn} from "../../../utils/ColumnUtils";
import {useForm} from "../../../utils/FormUtils";
import {userHasAnyRole} from "../../../utils/SecurityUtils";
import {nullableNumber, nullableString, optionalBool, requiredWerteListeObjekt} from "../../../utils/Validator";
import EAAccordion from "../../EAAccordion";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EACheckbox from "../../EACheckbox";
import EADialog from "../../EADialog";
import EAFieldset from "../../EAFieldset";
import EATable from "../../EATable";
import EATableActionButton from "../../tableComponents/EATableActionButton";
import {useHttpSortById} from "../../../utils/usePreformattedHttp";
import {useConfirmation} from "../../ConfirmationDialog/ConfirmationDialogProvider";
import {ENTLASTUNG} from "../../../consts/anlagentyp";

function TableDialog(props) {
    const [parameterOptionen] = useHttpSortById(getAsParameterEndpoint);
    const [einheitOptionen] = useHttpSortById(getAsEinheitEndpoint);

    return (
        <EADialog
            open={props.open}
            titleChildren={"Auslegung/Belastung"}
            content={
                <div style={{width: "600px"}}>
                    <div className="gridl" style={{gridTemplateColumns: "auto auto"}}>
                        {props.formFactory.renderAutocompleteWerteliste(
                            "parameter",
                            "parameter",
                            "Parameter",
                            parameterOptionen,
                            false,
                            false,
                            null,
                            [
                                {
                                    id: "einheit",
                                    calcDependentVal: () => {
                                        return null;
                                    },
                                },
                            ]
                        )}
                        {props.formFactory.renderAutocompleteWerteliste("einheit", "einheit", "Einheit", einheitOptionen)}
                        <EAFieldset caption={"Auslegung"} style={{gridColumnEnd: "span 2"}}>
                            <div className={"gridl"} style={{gridTemplateColumns: "1fr 1fr"}}>
                                {props.formFactory.renderNumberInput("auslegungWert", "auslegungWert", "Wert")}
                                {props.formFactory.renderInput("auslegungJahr", "auslegungJahr", "Jahr")}
                            </div>
                        </EAFieldset>
                        {props.formFactory.renderCheckbox("auslegungFuehrend", "auslegungFuehrend", "Führend", false, {
                            style: {gridColumnEnd: "span 2"},
                        })}
                        <EAFieldset caption={"Belastung"} style={{gridColumnEnd: "span 2"}}>
                            <div className={"gridl"} style={{gridTemplateColumns: "1fr 1fr"}}>
                                {props.formFactory.renderNumberInput("belastungWert", "belastungWert", "Wert")}
                                {props.formFactory.renderInput("belastungJahr", "belastungJahr", "Jahr")}
                            </div>
                        </EAFieldset>
                        {props.formFactory.renderInput("bemerkung", "bemerkung", "Bemerkung", "text", false, {
                            multiline: true,
                            rows: 10,
                            style: {gridColumnEnd: "span 2"},
                        })}
                    </div>
                </div>
            }
            buttons={
                <EAButtonBar>
                    <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={props.onClickCancel}/>
                    <EAButton id={"btnOk"} data-cy={"btnOk"} color="primary" label={"Übernehmen"} onClick={props.onClickOk}/>
                </EAButtonBar>
            }
        />
    );
}

TableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    formFactory: PropTypes.any.isRequired,
    data: PropTypes.any.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

export default function Auslegungen(props) {
    const confirm = useConfirmation();
    const [extended, setExtended] = useState(true);
    const dataName = "auslegungUndBelastungsDaten";
    const [tableIndex, setTableIndex] = useState(-1);
    const schema = {
        auslegungFuehrend: optionalBool(),
        auslegungJahr: nullableNumber()
            .min(1900)
            .max(2100)
            .error((tmpErrors) => {
                tmpErrors[0].message = "Das Jahr muss zwischen 1900 und 2100 liegen.";
                return tmpErrors[0];
            }),
        auslegungWert: nullableNumber(),
        belastungJahr: nullableNumber()
            .min(1900)
            .max(2100)
            .error((tmpErrors) => {
                tmpErrors[0].message = "Das Jahr muss zwischen 1900 und 2100 liegen.";
                return tmpErrors[0];
            }),
        belastungWert: nullableNumber(),
        einheit: requiredWerteListeObjekt(),
        parameter: requiredWerteListeObjekt(),
        bemerkung: nullableString(),
    };
    const {values, setValues, formFactory, validate, setErrors} = useForm({
        initialValues: {
            auslegungFuehrend: false,
            auslegungJahr: null,
            auslegungWert: null,
            belastungJahr: null,
            belastungWert: null,
            bemerkung: "",
            einheit: null,
            parameter: null,
        },
        onSubmit() {
        },
        schema,
    });
    const [dlgOpen, setDlgOpen] = useState(false);

    function handleDelete(index) {
        if (isDisabled()) {
            return;
        }
        confirm({
            title: "Entfernen bestätigen",
            description: "Wollen Sie die Auslegung wirklich entfernen? (Final gelöscht wird erst beim Speichern der Anlage.)",
            onSubmit: () => removeAuslegung(index),
            onClose: () => {
            },
        });
    }

    function removeAuslegung(index) {
        const tableData = [...props.data];
        tableData.splice(index, 1);
        setValues({});
        props.onChange(dataName, tableData);
    }

    function handleEdit(index) {
        logger.debug("Edit " + dataName);
        if (isDisabled()) {
            return;
        }
        setTableIndex(index);
        setValues(props.data[index]);
        setDlgOpen(true);
    }

    function handleClose() {
        setDlgOpen(false);
        setTableIndex(-1);
        setValues({});
    }

    function handleCloseOk() {
        const e = validate();
        if (e && Object.keys(e).length > 0) {
            return;
        }
        setDlgOpen(false);
        const tableData = [...props.data];
        if (tableIndex === -1) {
            if (values.auslegungFuehrend === true) {
                tableData.forEach((element) => {
                    element.auslegungFuehrend = false
                });
            }
            tableData.push({...values});
        } else {
            if (values.auslegungFuehrend !== tableData[tableIndex].auslegungFuehrend) {
                tableData.forEach((element) => {
                    element.auslegungFuehrend = false
                });
            }
            tableData[tableIndex] = {...tableData[tableIndex], ...values};
        }
        logger.debug(dataName + "Close", tableData);
        setTableIndex(-1);
        setValues({});
        props.onChange(dataName, tableData);
    }

    function isDisabled() {
        if (props.anlegen) return false;
        if (props.modus === "l") {
            return true;
        }
        if (props.modus === "s") {
            return !userHasAnyRole([FACHADMIN, SYSTEMADMIN, ZUST_SBD]);
        }
        return true;
    }

    useEffect(() => {
        setErrors({});
    }, [dlgOpen, setErrors])

    function getRoles() {
        if (props.anlagentyp === ENTLASTUNG) {
            return [FACHADMIN, SYSTEMADMIN, ZUST_SBD, BETREIBER];
        }
        return [FACHADMIN, SYSTEMADMIN, ZUST_SBD];
    }

    return (
        <React.Fragment>
            <TableDialog
                formFactory={formFactory}
                open={dlgOpen}
                data={{...values}}
                onClickCancel={handleClose}
                onClickOk={handleCloseOk}
            />
            <EAAccordion
                label={`Auslegung / Belastung (${props.data ? props.data.length : 0})`}
                className="gridl accordionPadding"
                id="auslegungUndBelastungAccordion"
                variant="light"
                expanded={extended}
                onChange={(e, expanded) => setExtended(expanded)}
            >
                <EATable
                    id="auslegungUndBelastung"
                    initialSortColumn="parameter"
                    columns={[
                        createComponentColumn(
                            "actions",
                            (row, index) => {
                                if (isDisabled()) return <div/>;
                                return (
                                    <Grid container spacing={1} justify="center">
                                        <Grid item>
                                            <EATableActionButton id={"editAuslegung-" + index} iconName="ico-edit">
                                                <span
                                                    onClick={() => handleEdit(index)}
                                                    className={"w-100"}
                                                    style={{
                                                        height: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                            </EATableActionButton>
                                        </Grid>
                                        <Grid item>
                                            <EATableActionButton id={"deleteAuslegung-" + index} iconName="ico-delete">
                                                <span
                                                    onClick={() => handleDelete(index)}
                                                    className={"w-100"}
                                                    style={{
                                                        height: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                            </EATableActionButton>
                                        </Grid>
                                    </Grid>
                                );
                            },
                            "",
                            null,
                            {width: "50px", padding: "0"}
                        ),
                        createCalculatedColumn(
                            "parameter",
                            (row) => {
                                return row.parameter && row.parameter.value ? row.parameter.value : "";
                            },
                            "Parameter"
                        ),
                        createCalculatedColumn(
                            "einheit",
                            (row) => {
                                return row.einheit && row.einheit.value ? row.einheit.value : "";
                            },
                            "Einheit",
                            {padding: 0}
                        ),
                        createNumberColumn("auslegungWert", "Auslegungswert", {padding: 0}),
                        createColumn("auslegungJahr", "Auslegungsjahr", {padding: 0}),
                        createCalculatedColumn(
                            "auslegungFuehrend",
                            (row) => {
                                return (
                                    <EACheckbox
                                        id="auslegungFuehrend"
                                        checked={row.auslegungFuehrend}
                                        children={null}
                                        disabled={true}
                                        style={{padding: "0", marginTop: "-4px", marginBottom: "-4px"}}
                                    />
                                );
                            },
                            "Führend",
                            {padding: 0}
                        ),
                        createNumberColumn("belastungWert", "Belastungswert", {padding: 0}),
                        createColumn("belastungJahr", "Belastungsjahr", {padding: 0}),
                    ]}
                    data={props.data}
                />
                {props.errors && props.errors.auslegungUndBelastungsDaten && (
                    <FormHelperText id="auslegungen-helper-text" error>
                        {props.errors.auslegungUndBelastungsDaten}
                    </FormHelperText>
                )}
                {userHasAnyRole(getRoles()) &&
                    props.formFactory.renderButton(
                        "addAuslegung",
                        "addAuslegung",
                        "Hinzufügen",
                        () => {
                            setTableIndex(-1);
                            setDlgOpen(true);
                        },
                        {
                            style: {placeSelf: "end"},
                        }
                    )}
            </EAAccordion>
        </React.Fragment>
    );
}

Auslegungen.propTypes = {
    data: PropTypes.any,
    errors: PropTypes.any,
    formFactory: PropTypes.any,
    onChange: PropTypes.func,
};
